import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FiDownload, FiAlertCircle } from 'react-icons/fi';
import './ClientFinancePortal.css';

const fmt = (v, c) => {
  try {
    const n = Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
    return `${n} ${c || ''}`.trim();
  } catch { return `${v} ${c || ''}`; }
};

// ---------- i18n ----------
const I18N = {
  en: {
    brand: 'AA Project Management',
    downloadPdf: 'Download Quotation PDF',
    eyebrow: 'Project Statement',
    forLabel: 'For',
    quotationTotal: 'Quotation Total',
    delivered: 'Delivered so far',
    paidByYou: 'Paid by you',
    outstanding: 'Outstanding',
    scope: 'Scope of Work',
    noItems: 'No items listed yet.',
    subtotal: 'Subtotal',
    itemSingular: 'item',
    itemPlural: 'items',
    descCol: 'Description',
    qtyCol: 'Qty',
    unitCol: 'Unit',
    amountCol: (c) => `Amount (${c})`,
    deliveredCol: (c) => `Delivered (${c})`,
    payments: 'Your Payments',
    noPayments: 'No payments received yet.',
    dateCol: 'Date',
    amountColPay: 'Amount',
    noteCol: 'Note',
    linkUnavailable: 'Link not available',
    loading: 'Loading…',
  },
  ar: {
    brand: 'AA لإدارة المشاريع',
    downloadPdf: 'تحميل عرض السعر PDF',
    eyebrow: 'كشف المشروع',
    forLabel: 'لـ',
    quotationTotal: 'إجمالي العرض',
    delivered: 'تم التنفيذ حتى الآن',
    paidByYou: 'المدفوع من قبلك',
    outstanding: 'المتبقي',
    scope: 'بنود العمل',
    noItems: 'لا توجد بنود حتى الآن.',
    subtotal: 'المجموع الفرعي',
    itemSingular: 'بند',
    itemPlural: 'بنود',
    descCol: 'البيان',
    qtyCol: 'الكمية',
    unitCol: 'الوحدة',
    amountCol: (c) => `الإجمالي (${c})`,
    deliveredCol: (c) => `المنفذ (${c})`,
    payments: 'دفعاتك',
    noPayments: 'لم يتم استلام أي دفعات بعد.',
    dateCol: 'التاريخ',
    amountColPay: 'المبلغ',
    noteCol: 'ملاحظة',
    linkUnavailable: 'الرابط غير متاح',
    loading: 'جارٍ التحميل…',
  },
  tr: {
    brand: 'AA Proje Yönetimi',
    downloadPdf: 'Teklif PDF\u2019sini İndir',
    eyebrow: 'Proje Özeti',
    forLabel: 'İçin',
    quotationTotal: 'Teklif Tutarı',
    delivered: 'Şu ana kadar yapılan',
    paidByYou: 'Ödediğiniz',
    outstanding: 'Bakiye',
    scope: 'İş Kapsamı',
    noItems: 'Henüz kalem listelenmedi.',
    subtotal: 'Ara Toplam',
    itemSingular: 'kalem',
    itemPlural: 'kalem',
    descCol: 'Açıklama',
    qtyCol: 'Miktar',
    unitCol: 'Birim',
    amountCol: (c) => `Tutar (${c})`,
    deliveredCol: (c) => `Yapılan (${c})`,
    payments: 'Ödemeleriniz',
    noPayments: 'Henüz ödeme alınmadı.',
    dateCol: 'Tarih',
    amountColPay: 'Tutar',
    noteCol: 'Not',
    linkUnavailable: 'Bağlantı kullanılamıyor',
    loading: 'Yükleniyor…',
  },
};

const ClientFinancePortal = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/finance/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const lang = (data?.financeProject?.language || 'en').toLowerCase();
  const t = I18N[lang] || I18N.en;
  const isRtl = lang === 'ar';

  // Apply RTL direction to document while portal is open
  useEffect(() => {
    if (!data) return;
    const prev = document.documentElement.getAttribute('dir');
    document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
    return () => {
      if (prev !== null) document.documentElement.setAttribute('dir', prev);
      else document.documentElement.removeAttribute('dir');
      document.documentElement.removeAttribute('lang');
    };
  }, [data, isRtl, lang]);

  const downloadPdf = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/finance/${token}/quotation.pdf`, '_blank');
  };

  const itemsByGroup = useMemo(() => {
    const map = {};
    for (const it of (data?.items || [])) {
      const gid = it.financeGroupId || '_ungrouped';
      (map[gid] = map[gid] || []).push(it);
    }
    return map;
  }, [data]);

  if (loading) {
    return <div className="cfp-loading" data-testid="cfp-loading">{t.loading}</div>;
  }
  if (error) {
    return (
      <div className="cfp-error" data-testid="cfp-error">
        <FiAlertCircle size={48} />
        <h2>{t.linkUnavailable}</h2>
        <p>{error}</p>
      </div>
    );
  }
  if (!data) return null;

  const { financeProject: fp, groups, projectClientPayments, totals } = data;
  const base = totals.baseCurrency;

  return (
    <div className={`cfp-page ${isRtl ? 'cfp-rtl' : ''}`} data-testid="client-finance-portal" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="cfp-shell">
        {/* Brand header */}
        <header className="cfp-header">
          <div className="cfp-brand">
            <div className="cfp-brand-dot" />
            <span className="cfp-brand-name">{t.brand}</span>
          </div>
          <button type="button" className="cfp-btn cfp-btn-dark" onClick={downloadPdf} data-testid="cfp-download-pdf">
            <FiDownload size={14} /> {t.downloadPdf}
          </button>
        </header>

        {/* Title */}
        <section className="cfp-title-section">
          <div>
            <div className="cfp-eyebrow">{t.eyebrow}</div>
            <h1 className="cfp-title" data-testid="cfp-title">{fp.name}</h1>
            {fp.client && <div className="cfp-client">{t.forLabel}: <strong>{fp.client}</strong></div>}
            {fp.description && <p className="cfp-desc">{fp.description}</p>}
          </div>
        </section>

        {/* Top totals */}
        <section className="cfp-totals">
          <Stat label={t.quotationTotal} value={fmt(totals.sellBase, base)} tone="dark" testId="cfp-total" />
          <Stat label={t.delivered} value={fmt(totals.clientBilledBase, base)} tone="blue" testId="cfp-billed" />
          <Stat label={t.paidByYou} value={fmt(totals.clientReceivedBase, base)} tone="green" testId="cfp-paid" />
          <Stat label={t.outstanding} value={fmt(totals.clientOutstandingBase, base)} tone="amber" testId="cfp-outstanding" />
        </section>

        {/* Groups + items */}
        <section className="cfp-section">
          <h2>{t.scope}</h2>
          {(groups || []).length === 0 ? (
            <div className="cfp-empty">{t.noItems}</div>
          ) : (
            <div className="cfp-groups">
              {groups.map((g) => {
                const gItems = itemsByGroup[g.id] || [];
                if (gItems.length === 0) return null;
                return (
                  <div className="cfp-group" key={g.id} data-testid={`cfp-group-${g.id}`}>
                    <div className="cfp-group-header" style={isRtl ? { borderRightColor: g.color || '#0F0F10', borderLeftColor: 'transparent' } : { borderLeftColor: g.color || '#0F0F10' }}>
                      <span className="cfp-group-dot" style={{ background: g.color || '#0F0F10' }} />
                      <span className="cfp-group-name">{g.name}</span>
                      <span className="cfp-group-meta">{gItems.length} {gItems.length === 1 ? t.itemSingular : t.itemPlural}</span>
                      <span className="cfp-group-subtotal">
                        <span className="muted">{t.subtotal}</span> <strong>{fmt(g.sellBase, base)}</strong>
                      </span>
                    </div>
                    <table className="cfp-items">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          <th>{t.descCol}</th>
                          <th style={{ width: '70px' }} className="text-right">{t.qtyCol}</th>
                          <th style={{ width: '70px' }}>{t.unitCol}</th>
                          <th style={{ width: '140px' }} className="text-right">{t.amountCol(base)}</th>
                          <th style={{ width: '140px' }} className="text-right">{t.deliveredCol(base)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gItems.map((it, i) => (
                          <tr key={it.id} data-testid={`cfp-item-${it.id}`}>
                            <td className="muted">{i + 1}</td>
                            <td>
                              <strong>{it.name}</strong>
                            </td>
                            <td className="text-right">{Number(it.quantity || 1)}</td>
                            <td>{it.unit || '—'}</td>
                            <td className="text-right"><strong>{fmt(it.sellBase, base)}</strong></td>
                            <td className="text-right cfp-billed-cell">{fmt(it.clientBilledBase, base)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Payments */}
        <section className="cfp-section">
          <h2>{t.payments}</h2>
          {(projectClientPayments || []).length === 0 ? (
            <div className="cfp-empty">{t.noPayments}</div>
          ) : (
            <table className="cfp-payments">
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>{t.dateCol}</th>
                  <th style={{ width: '180px' }} className="text-right">{t.amountColPay}</th>
                  <th>{t.noteCol}</th>
                </tr>
              </thead>
              <tbody>
                {projectClientPayments.map((p) => (
                  <tr key={p.id} data-testid={`cfp-pay-${p.id}`}>
                    <td>{p.date || '—'}</td>
                    <td className="text-right cfp-paid-cell">{fmt(p.amount, p.currency)}</td>
                    <td className="muted">{p.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <footer className="cfp-footer">
          {t.brand} · info@aa-do.com · +90 505 998 8292
        </footer>
      </div>
    </div>
  );
};

const Stat = ({ label, value, tone, testId }) => (
  <div className={`cfp-stat cfp-stat-${tone || ''}`} data-testid={testId}>
    <div className="cfp-stat-label">{label}</div>
    <div className="cfp-stat-value">{value}</div>
  </div>
);

export default ClientFinancePortal;
